<template>
    <FloatingPopup
        id="icon-editor-popup"
        :model-value="true"
        title="Icon Editor"
        :width="580"
        :height="560"
        :minwidth="400"
        :minheight="400"
        :opacity="100"
        @update:model-value="$emit('cancel')"
    >
        <div class="flex flex-col gap-2 h-full" style="user-select:none">

            <!-- Toolbar + canvas row -->
            <div class="flex gap-2">

                <!-- Left tool strip -->
                <div class="flex flex-col gap-px flex-shrink-0">
                    <button
                        v-for="t in tools" :key="t.id"
                        :title="t.label"
                        @click="activeTool = t.id"
                        class="w-9 h-9 rounded text-lg flex items-center justify-center transition-colors"
                        :class="activeTool === t.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-300'"
                    >{{ t.icon }}</button>

                    <div class="h-px bg-gray-600 mx-1 my-0.5"></div>

                    <button title="Undo (Ctrl+Z)" @click="undo" :disabled="historyIndex <= 0"
                        class="w-9 h-9 rounded text-sm flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed">↩</button>
                    <button title="Redo (Ctrl+Y)" @click="redo" :disabled="historyIndex >= history.length - 1"
                        class="w-9 h-9 rounded text-sm flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed">↪</button>
                    <button title="Clear canvas" @click="clear"
                        class="w-9 h-9 rounded text-sm flex items-center justify-center bg-gray-700 hover:bg-red-900 text-gray-300">🗑</button>
                </div>

                <!-- Konva stage -->
                <div
                    ref="stageWrap"
                    class="rounded border border-gray-600 overflow-hidden flex-shrink-0"
                    style="width:380px;height:380px;background:#1e1e2e;"
                ></div>
            </div>

            <!-- Options bar -->
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-300">
                <!-- Foreground color -->
                <label class="flex items-center gap-1.5 cursor-pointer">
                    <span>Color</span>
                    <span
                        class="w-7 h-5 rounded border border-gray-500 cursor-pointer inline-block"
                        :style="{ background: primaryColor }"
                        @click="$refs.colorInput.click()"
                        title="Click to change color"
                    ></span>
                    <input ref="colorInput" type="color" v-model="primaryColor" class="sr-only" />
                </label>

                <!-- Opacity -->
                <label class="flex items-center gap-1.5">
                    <span>Alpha {{ Math.round(opacity / 2.55) }}%</span>
                    <input type="range" v-model.number="opacity" min="0" max="255" step="1" class="w-20 h-1 accent-blue-500" />
                </label>

                <!-- Brush size -->
                <label v-if="!['fill','eyedropper'].includes(activeTool)" class="flex items-center gap-1.5">
                    <span>Size {{ strokeWidth }}</span>
                    <input type="range" v-model.number="strokeWidth" min="1" max="16" step="1" class="w-16 h-1 accent-blue-500" />
                </label>

                <!-- Fill toggle -->
                <label v-if="['rect','ellipse'].includes(activeTool)" class="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" v-model="fillShape" class="accent-blue-500" />
                    <span>Fill</span>
                </label>

                <span class="ml-auto text-gray-500 tabular-nums" style="font-size:10px">{{ cursorPos }}</span>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-2 pt-1 border-t border-gray-700">
                <button @click="$emit('cancel')" class="px-4 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm">Cancel</button>
                <button @click="saveIcon"         class="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">Save Icon</button>
            </div>
        </div>
    </FloatingPopup>
</template>

<script>
import Konva from 'konva';
import FloatingPopup from '../FloatingPopup.vue';

const SIZE    = 64;   // internal pixel resolution
const DISPLAY = 380;  // CSS display size (≈6× zoom)
const SCALE   = DISPLAY / SIZE;

export default {
    name: 'IconEditorModal',
    components: { FloatingPopup },

    props: {
        initialIcon: { type: String, default: null },
    },
    emits: ['save', 'cancel'],

    data() {
        return {
            activeTool:  'pen',
            primaryColor: '#ffffff',
            opacity:      255,
            strokeWidth:  3,
            fillShape:    false,
            isDrawing:    false,
            startX: 0, startY: 0,
            lastX:  0, lastY:  0,
            snapshotData: null,
            history:      [],
            historyIndex: -1,
            cursorPos:    '',
            stage:        null,
            drawLayer:    null,
            konvaImage:   null,
            offscreen:    null,      // HTMLCanvasElement — pixel operations
            tools: [
                { id: 'pen',        icon: '✏',  label: 'Pen (freehand)'  },
                { id: 'eraser',     icon: '⌫',  label: 'Eraser'          },
                { id: 'line',       icon: '╱',  label: 'Line'            },
                { id: 'rect',       icon: '▭',  label: 'Rectangle'       },
                { id: 'ellipse',    icon: '○',  label: 'Ellipse'         },
                { id: 'fill',       icon: '◼',  label: 'Fill Bucket'     },
                { id: 'eyedropper', icon: '⊕',  label: 'Pick Color'      },
            ],
        };
    },

    computed: {
        drawColor() {
            const r = parseInt(this.primaryColor.slice(1, 3), 16);
            const g = parseInt(this.primaryColor.slice(3, 5), 16);
            const b = parseInt(this.primaryColor.slice(5, 7), 16);
            return `rgba(${r},${g},${b},${this.opacity / 255})`;
        },
    },

    mounted() {
        this.$nextTick(() => this.initKonva());
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('mouseup',  this.onGlobalMouseUp);
    },

    beforeUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('mouseup',  this.onGlobalMouseUp);
        if (this.stage) this.stage.destroy();
    },

    methods: {
        // ── Init ──────────────────────────────────────────────────────────────

        initKonva() {
            // Offscreen canvas — all pixel operations happen here
            this.offscreen        = document.createElement('canvas');
            this.offscreen.width  = SIZE;
            this.offscreen.height = SIZE;

            // Konva stage
            this.stage = new Konva.Stage({
                container: this.$refs.stageWrap,
                width:  DISPLAY,
                height: DISPLAY,
            });

            // Solid background layer — prevents app UI showing through transparent pixels
            const bgLayer = new Konva.Layer({ listening: false });
            bgLayer.add(new Konva.Rect({
                x: 0, y: 0, width: DISPLAY, height: DISPLAY,
                fill: '#1e1e2e',
            }));
            this.stage.add(bgLayer);

            this.drawLayer = new Konva.Layer();
            this.konvaImage = new Konva.Image({
                image:  this.offscreen,
                width:  DISPLAY,
                height: DISPLAY,
                listening: true,
            });
            this.drawLayer.add(this.konvaImage);
            this.stage.add(this.drawLayer);

            // Events
            this.stage.on('mousedown',  this.onMouseDown);
            this.stage.on('mousemove',  this.onMouseMove);
            this.stage.on('mouseup',    this.onMouseUp);
            this.stage.on('mouseleave', this.onMouseLeave);

            // Load initial image if provided
            if (this.initialIcon) {
                const img = new Image();
                img.onload = () => {
                    this.offscreen.getContext('2d').drawImage(img, 0, 0, SIZE, SIZE);
                    this.refresh();
                    this.pushHistory();
                };
                img.src = this.initialIcon;
            } else {
                this.pushHistory();
            }
        },

        refresh() {
            this.konvaImage.image(this.offscreen);
            this.drawLayer.batchDraw();
        },

        // ── Coordinates ───────────────────────────────────────────────────────

        stagePos() {
            const p = this.stage.getPointerPosition();
            if (!p) return null;
            return {
                x: Math.max(0, Math.min(SIZE - 1, Math.floor(p.x / SCALE))),
                y: Math.max(0, Math.min(SIZE - 1, Math.floor(p.y / SCALE))),
            };
        },

        // ── History ───────────────────────────────────────────────────────────

        pushHistory() {
            const data = this.offscreen.getContext('2d').getImageData(0, 0, SIZE, SIZE);
            this.history.splice(this.historyIndex + 1);
            this.history.push(data);
            if (this.history.length > 50) this.history.shift();
            this.historyIndex = this.history.length - 1;
        },

        undo() {
            if (this.historyIndex <= 0) return;
            this.historyIndex--;
            this.offscreen.getContext('2d').putImageData(this.history[this.historyIndex], 0, 0);
            this.refresh();
        },

        redo() {
            if (this.historyIndex >= this.history.length - 1) return;
            this.historyIndex++;
            this.offscreen.getContext('2d').putImageData(this.history[this.historyIndex], 0, 0);
            this.refresh();
        },

        clear() {
            this.offscreen.getContext('2d').clearRect(0, 0, SIZE, SIZE);
            this.refresh();
            this.pushHistory();
        },

        // ── Draw context setup ────────────────────────────────────────────────

        applyStyle(ctx) {
            ctx.lineCap   = 'round';
            ctx.lineJoin  = 'round';
            ctx.lineWidth = this.strokeWidth;
            if (this.activeTool === 'eraser') {
                ctx.globalCompositeOperation = 'destination-out';
                ctx.strokeStyle = 'rgba(0,0,0,1)';
                ctx.fillStyle   = 'rgba(0,0,0,1)';
            } else {
                ctx.globalCompositeOperation = 'source-over';
                ctx.strokeStyle = this.drawColor;
                ctx.fillStyle   = this.drawColor;
            }
        },

        resetStyle(ctx) {
            ctx.globalCompositeOperation = 'source-over';
        },

        // ── Konva mouse events ────────────────────────────────────────────────

        onMouseDown() {
            const pos = this.stagePos();
            if (!pos) return;
            const { x, y } = pos;
            const ctx = this.offscreen.getContext('2d');

            // Update stage cursor
            this.stage.container().style.cursor =
                this.activeTool === 'eyedropper' ? 'crosshair' :
                this.activeTool === 'fill'        ? 'cell' : 'crosshair';

            if (this.activeTool === 'eyedropper') {
                const px = ctx.getImageData(x, y, 1, 1).data;
                this.primaryColor = '#' + [px[0], px[1], px[2]]
                    .map(v => v.toString(16).padStart(2, '0')).join('');
                this.opacity = px[3];
                return;
            }

            if (this.activeTool === 'fill') {
                this.floodFill(ctx, x, y);
                this.refresh();
                this.pushHistory();
                return;
            }

            this.isDrawing    = true;
            this.startX = x; this.startY = y;
            this.lastX  = x; this.lastY  = y;
            this.snapshotData = ctx.getImageData(0, 0, SIZE, SIZE);

            if (this.activeTool === 'pen' || this.activeTool === 'eraser') {
                this.applyStyle(ctx);
                ctx.beginPath();
                ctx.arc(x, y, this.strokeWidth / 2, 0, Math.PI * 2);
                ctx.fill();
                this.refresh();
            }
        },

        onMouseMove() {
            const pos = this.stagePos();
            if (!pos) return;
            const { x, y } = pos;
            this.cursorPos = `${x}, ${y}`;

            if (!this.isDrawing) return;
            const ctx = this.offscreen.getContext('2d');
            this.applyStyle(ctx);

            if (this.activeTool === 'pen' || this.activeTool === 'eraser') {
                ctx.beginPath();
                ctx.moveTo(this.lastX, this.lastY);
                ctx.lineTo(x, y);
                ctx.stroke();
                this.lastX = x; this.lastY = y;
            } else {
                ctx.putImageData(this.snapshotData, 0, 0);
                this.applyStyle(ctx);
                this.drawShape(ctx, x, y);
            }
            this.refresh();
        },

        onMouseUp() {
            if (!this.isDrawing) return;
            this.isDrawing = false;
            const pos = this.stagePos();
            const ctx = this.offscreen.getContext('2d');

            if (pos && !['pen', 'eraser'].includes(this.activeTool)) {
                ctx.putImageData(this.snapshotData, 0, 0);
                this.applyStyle(ctx);
                this.drawShape(ctx, pos.x, pos.y);
                this.refresh();
            }

            this.resetStyle(ctx);
            this.snapshotData = null;
            this.pushHistory();
        },

        onMouseLeave() {
            this.cursorPos = '';
            if (this.isDrawing && (this.activeTool === 'pen' || this.activeTool === 'eraser')) {
                this.isDrawing = false;
                this.resetStyle(this.offscreen.getContext('2d'));
                this.pushHistory();
            }
        },

        onGlobalMouseUp() {
            if (this.isDrawing && !['pen','eraser'].includes(this.activeTool)) {
                this.onMouseUp();
            }
        },

        // ── Shape drawing ─────────────────────────────────────────────────────

        drawShape(ctx, x, y) {
            const sx = this.startX, sy = this.startY;
            ctx.beginPath();
            switch (this.activeTool) {
                case 'line':
                    ctx.moveTo(sx, sy);
                    ctx.lineTo(x, y);
                    ctx.stroke();
                    break;

                case 'rect':
                    if (this.fillShape) ctx.fillRect(sx, sy, x - sx, y - sy);
                    else               ctx.strokeRect(sx + 0.5, sy + 0.5, x - sx, y - sy);
                    break;

                case 'ellipse': {
                    const cx = (sx + x) / 2, cy = (sy + y) / 2;
                    const rx = Math.abs(x - sx) / 2, ry = Math.abs(y - sy) / 2;
                    if (rx < 1 || ry < 1) break;
                    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
                    if (this.fillShape) ctx.fill(); else ctx.stroke();
                    break;
                }
            }
        },

        // ── Flood fill (BFS on offscreen canvas) ──────────────────────────────

        floodFill(ctx, startX, startY) {
            const S   = SIZE;
            const img = ctx.getImageData(0, 0, S, S);
            const d   = img.data;
            const fi  = (startX + startY * S) * 4;
            const tr = d[fi], tg = d[fi+1], tb = d[fi+2], ta = d[fi+3];
            const fr = parseInt(this.primaryColor.slice(1, 3), 16);
            const fg = parseInt(this.primaryColor.slice(3, 5), 16);
            const fb = parseInt(this.primaryColor.slice(5, 7), 16);
            const fa = this.opacity;
            if (tr === fr && tg === fg && tb === fb && ta === fa) return;

            const visited = new Uint8Array(S * S);
            const stack   = [startX + startY * S];
            visited[stack[0]] = 1;

            while (stack.length) {
                const p  = stack.pop();
                const i  = p * 4;
                d[i] = fr; d[i+1] = fg; d[i+2] = fb; d[i+3] = fa;
                const px = p % S, py = (p - px) / S;
                if (px > 0   && !visited[p-1]  && d[(p-1)*4]===tr  && d[(p-1)*4+1]===tg && d[(p-1)*4+2]===tb && d[(p-1)*4+3]===ta) { visited[p-1]=1;  stack.push(p-1); }
                if (px < S-1 && !visited[p+1]  && d[(p+1)*4]===tr  && d[(p+1)*4+1]===tg && d[(p+1)*4+2]===tb && d[(p+1)*4+3]===ta) { visited[p+1]=1;  stack.push(p+1); }
                if (py > 0   && !visited[p-S]  && d[(p-S)*4]===tr  && d[(p-S)*4+1]===tg && d[(p-S)*4+2]===tb && d[(p-S)*4+3]===ta) { visited[p-S]=1;  stack.push(p-S); }
                if (py < S-1 && !visited[p+S]  && d[(p+S)*4]===tr  && d[(p+S)*4+1]===tg && d[(p+S)*4+2]===tb && d[(p+S)*4+3]===ta) { visited[p+S]=1;  stack.push(p+S); }
            }
            ctx.putImageData(img, 0, 0);
        },

        // ── Output ────────────────────────────────────────────────────────────

        saveIcon() {
            this.$emit('save', this.offscreen.toDataURL('image/png'));
        },

        onKeyDown(e) {
            if (e.key === 'Escape') { this.$emit('cancel'); return; }
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') { e.preventDefault(); this.undo(); }
            if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'Z' || e.key === 'y')) { e.preventDefault(); this.redo(); }
        },
    },
};
</script>
